import { create } from 'zustand';

import userApi from '../../api/user';
import RemoteDataStatus from '../../types/RemoteDataStatus';

export enum ActionType {
  TYPEFACE_UPDATED = 'typefaceUpdated',
  MOCKUP_MODE_VISITED = 'mockupModeVisited',
  ELEMENT_IMPORTED = 'elementImported',
  FIRST_WORKSPACE_INVITE = 'firstWorkspaceInvite',
}

export type ActionKey = ActionType | string;

export interface ActionStore {
  status: RemoteDataStatus;
  actions: ReadonlyArray<{ id: ActionKey; createdAt: Date }> | null;
  createAction: (action: ActionKey) => Promise<void>;
  deleteActionLocally: (action: ActionKey) => void;
  init: () => Promise<void>;
  reset: () => void;
  refresh: () => Promise<void>;
  _loader: Promise<void> | undefined;
}

const initialState = {
  status: RemoteDataStatus.Initial,
  actions: null,
  _loader: undefined,
};

const parseActions = (
  actions: { id: ActionKey; createdAt: string }[]
): { id: ActionKey; createdAt: Date }[] => {
  return actions.map((action) => ({
    id: action.id,
    createdAt: new Date(action.createdAt),
  }));
};

export const useActionStore = create<ActionStore>((set, get) => ({
  ...initialState,
  createAction: async (action): Promise<void> => {
    const response = await userApi.createAction(action);

    if ('results' in response) {
      set({ actions: parseActions(response.results) });
    }
  },
  init: async (): Promise<void> => {
    if (get().status !== RemoteDataStatus.Initial) {
      return get()._loader;
    }

    const loader = userApi
      .getActions()
      .then((response) => {
        if ('results' in response) {
          set({
            status: RemoteDataStatus.Done,
            actions: parseActions(response.results),
          });
        } else {
          console.error(`failed to fetch actions: ${response.error}`);

          set({ status: RemoteDataStatus.Error });
        }
      })
      .catch((e) => {
        console.error(`failed to fetch actions: ${e}`);

        set({ status: RemoteDataStatus.Error });
      });

    set({
      status: RemoteDataStatus.Loading,
      _loader: loader,
    });

    return loader;
  },
  reset: (): void => {
    set(initialState);
  },

  /**
   * Delete an action locally for some temporary reasons
   * please note the actions will be restored on the next re-init of this store
   */
  deleteActionLocally: (action): void => {
    set((state) => {
      if (!state.actions) {
        return state;
      }

      return {
        ...state,
        actions: state.actions.filter(({ id }) => id !== action),
      };
    });
  },
  refresh: async (): Promise<void> => {
    const response = await userApi.getActions();
    if ('results' in response) {
      set({
        status: RemoteDataStatus.Done,
        actions: parseActions(response.results),
      });
    } else {
      set({ status: RemoteDataStatus.Error });
    }
  },
}));

export const actionStoreSelector = {
  status: (state: ActionStore): ActionStore['status'] => state.status,
  actions: (state: ActionStore): ActionStore['actions'] => state.actions,
  actionIds: (state: ActionStore): ActionKey[] | null =>
    state.actions?.map(({ id }) => id) ?? null,
  createAction: (state: ActionStore): ActionStore['createAction'] =>
    state.createAction,
  deleteActionLocally: (
    state: ActionStore
  ): ActionStore['deleteActionLocally'] => state.deleteActionLocally,
  init: (state: ActionStore): ActionStore['init'] => state.init,
  reset: (state: ActionStore): ActionStore['reset'] => state.reset,
  refresh: (state: ActionStore): ActionStore['refresh'] => state.refresh,
};

/**
 * create an action if it does not exist in the store
 */
export const createActionIfNotExists = (action: ActionKey): void => {
  const actions = useActionStore.getState().actions;
  if (actions && !actions.find(({ id }) => id === action)) {
    void useActionStore.getState().createAction(action);
  }
};
